import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import InboxView from "../../components/InboxView";
import useActiveTenants from "../../hooks/useActiveTenants";
import { circlesApi } from "./circlesApi";
import { CHANNEL_LABELS, Modal, StatusBadge, phoneText, shortDate, useBusy, useCirclesList } from "./circlesShared";
import "../../assets/css/PageHeading.css";

const TABS = [
  { id: "New", label: "New", tone: "#2563eb" },
  { id: "Converted", label: "Converted", tone: "#16a34a" },
  { id: "Rejected", label: "Rejected", tone: "#64748b" },
  { id: "Duplicate", label: "Duplicate", tone: "#94a3b8" },
  { id: "", label: "All", tone: "#1e3a8a" },
];

function sourceText(r) {
  if (r.Source === "Circles") {
    const via = r.Channel ? CHANNEL_LABELS[r.Channel] || r.Channel : "Link";
    return `${r.Agent_Name || "Agent"} · ${via}${r.Share_Code ? ` · ${r.Share_Code}` : ""}`;
  }
  return r.Source;
}

function ConvertModal({ item, onClose, onDone }) {
  const tenants = useActiveTenants();
  const [tenantId, setTenantId] = useState(item.Tenant_Id ? String(item.Tenant_Id) : "");
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const { busy, error, run } = useBusy();

  useEffect(() => {
    let alive = true;
    if (!tenantId) return undefined;
    circlesApi
      .tenantProjects(tenantId)
      .then((rows) => alive && setProjects(rows))
      .catch(() => alive && setProjects([]));
    return () => {
      alive = false;
    };
  }, [tenantId]);

  const submit = () =>
    run(async () => {
      const res = await circlesApi.convertInbox(item.Inbox_Id, { tenant_id: Number(tenantId), tenant_project_id: projectId ? Number(projectId) : null });
      onDone(res.data);
    });

  return (
    <Modal
      title={`Convert ${item.Ref}`}
      onClose={onClose}
      busy={busy}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="wl-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="wl-btn wl-btn--primary" disabled={busy || !tenantId}>
            Create client lead
          </button>
        </>
      }
    >
      <p className="wl-muted">
        {item.Name} · {phoneText(item.Mobile)}
        {item.Project_Name ? ` · ${item.Project_Name}` : ""}
      </p>
      <label className="wl-field">
        <span>Client</span>
        <select value={tenantId} onChange={(e) => {
          setTenantId(e.target.value);
          setProjectId("");
          setProjects([]);
        }}>
          <option value="">Choose a client…</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      <label className="wl-field">
        <span>Client's project (optional)</span>
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={!tenantId}>
          <option value="">{item.Project_Name ? `Use "${item.Project_Name}"` : "Choose a project…"}</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>
      <p className="cx-hint">The lead appears in the client's portal as "Digix Leads". The agent stays hidden from the client.</p>
      {error ? <div className="wl-alert wl-alert--error">{error}</div> : null}
    </Modal>
  );
}

function RejectModal({ item, onClose, onDone }) {
  const [note, setNote] = useState("");
  const { busy, error, run } = useBusy();
  const submit = () =>
    run(async () => {
      await circlesApi.rejectInbox(item.Inbox_Id, note);
      onDone();
    });
  return (
    <Modal
      title={`Reject ${item.Ref}`}
      onClose={onClose}
      busy={busy}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="wl-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="wl-btn wl-btn--danger" disabled={busy}>
            Reject
          </button>
        </>
      }
    >
      <label className="wl-field">
        <span>Reason (the agent sees it)</span>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Not interested, wrong number" />
      </label>
      {error ? <div className="wl-alert wl-alert--error">{error}</div> : null}
    </Modal>
  );
}

export default function CirclesInbox() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("New");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [converting, setConverting] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { rows, extra, loading, error, reload } = useCirclesList(() => circlesApi.inbox({ status: tab || undefined, q: query || undefined }), [tab, query]);

  const actions = (r) =>
    r.Status === "New" ? (
      <div className="cx-actions">
        <button type="button" className="wl-btn wl-btn--primary wl-btn--sm" onClick={() => setConverting(r)}>
          Convert
        </button>
        <button type="button" className="wl-btn wl-btn--sm" onClick={() => setRejecting(r)}>
          Reject
        </button>
      </div>
    ) : r.Client_Lead_Id ? (
      <button type="button" className="wl-link-btn" onClick={() => navigate(`/realestate-leads/${r.Client_Lead_Id}/view`)}>
        Open lead
      </button>
    ) : (
      <span className="wl-sub">{r.Note || "—"}</span>
    );

  return (
    <div className="p-2 p-md-3">
      <div className="co-page-head">
        <h2 className="co-page-head__title">Digix Leads Inbox</h2>
      </div>
      <p className="cx-hint mb-2">Every Circles enquiry and Leads Sourcing import arrives here. Convert it into a lead for the right client.</p>
      <InboxView
        tabs={TABS}
        counts={extra.counts || {}}
        tab={tab}
        onTab={setTab}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search name, mobile or city"
        onRefresh={reload}
        notice={notice}
        onDismissNotice={() => setNotice(null)}
        error={error}
        loading={loading}
        rows={rows}
        rowKey={(r) => r.Inbox_Id}
        empty="No inbox items here."
        columns={[
          { key: "ref", label: "Item", render: (r) => (<><div className="wl-name">{r.Ref}</div><div className="wl-sub">{shortDate(r.Created_On)}</div></>) },
          { key: "customer", label: "Customer", render: (r) => (<><div className="wl-name">{r.Name}</div><div className="wl-sub">{phoneText(r.Mobile)}{r.City ? ` · ${r.City}` : ""}</div></>) },
          { key: "source", label: "Source", render: (r) => sourceText(r) },
          { key: "project", label: "Project · client", render: (r) => (<><div>{r.Project_Name || "—"}</div><div className="wl-sub">{r.Tenant_Name || "Client not set"}</div></>) },
          { key: "status", label: "Status", render: (r) => <StatusBadge status={r.Status} /> },
          { key: "actions", label: "Actions", className: "wl-actions-col", render: actions },
        ]}
        mobileCard={(r) => ({
          title: r.Name,
          meta: [{ label: r.Status, tone: r.Status === "New" ? "warning" : r.Status === "Converted" ? "success" : "neutral" }],
          fields: [
            { label: "Item", value: `${r.Ref} · ${shortDate(r.Created_On)}` },
            { label: "Mobile", value: phoneText(r.Mobile) },
            { label: "Source", value: sourceText(r) },
            { label: "Project", value: [r.Project_Name, r.Tenant_Name].filter(Boolean).join(" · ") },
          ],
          actions: actions(r),
        })}
      />
      {converting ? (
        <ConvertModal
          item={converting}
          onClose={() => setConverting(null)}
          onDone={(data) => {
            setConverting(null);
            setNotice({ type: "success", text: `${converting.Ref} is now client lead #${data.Client_Lead_Id}.` });
            reload();
          }}
        />
      ) : null}
      {rejecting ? (
        <RejectModal
          item={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null);
            setNotice({ type: "success", text: `${rejecting.Ref} was rejected.` });
            reload();
          }}
        />
      ) : null}
    </div>
  );
}
